import type { ChangeEvent, ComponentType, ReactNode } from 'react';
import { StarIcon } from '../../assets/icons/StarIcon';
import { PlusIcon } from '../../assets/icons/PlusIcon';
import { getMediaOrigin, type ReviewImageFile, type ReviewImagePreview } from '@skin-seoul/service';
import Select from './Select';

// Image path constants for S3
const REVIEW_IMAGE_PATHS = {
  COUPON: '/review/coupon.png',
} as const;
import { Textarea, Input, RadioGroup, RadioGroupItem, LucideIcon, useBreakpointValue } from '@skin-seoul/design-system';
import {
  Section,
  Star,
  FileInput,
  Row,
  SubmitButton,
  ImageUploadBox,
  Thumbnail,
  ThumbnailImage,
  RemoveButton,
  UploadArea,
  SmallUploadArea,
} from './SharedStyles';
import { decodeHTMLEntities } from '@skin-seoul/utils';

interface SectionProps {
  children: ReactNode;
  style?: React.CSSProperties;
}

interface RenderProductIntroProps {
  productInfo: {
    name: string;
    brand: string;
    image: string;
    category: string;
  };
}

interface RenderRatingSectionProps {
  rating: number;
  setRating: (rating: number) => void;
  SectionComponent?: ComponentType<SectionProps>;
}

interface RenderSkinInfoSectionProps {
  skinType: string;
  setSkinType: (value: string) => void;
  skinConcern: string;
  setSkinConcern: (value: string) => void;
  skinTypeOptions: Array<{ value: string; label: string }>;
  skinConcernOptions: Array<{ value: string; label: string }>;
}

interface RenderReviewContentSectionProps {
  content: string;
  setContent: (content: string) => void;
}

interface RenderTitleSectionProps {
  title: string;
  setTitle: (title: string) => void;
}

interface RenderImageUploadSectionProps {
  images: ReviewImageFile[];
  imagePreviews: ReviewImagePreview[];
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (id: string) => void;
}

interface RenderRecommendationSectionProps {
  recommend: string;
  setRecommend: (value: string) => void;
}

interface RenderNicknameSectionProps {
  nickname: string;
  setNickname: (nickname: string) => void;
}

interface RenderEmailSectionProps {
  email: string;
  setEmail: (email: string) => void;
}

interface RenderSubmitSectionProps {
  isSubmitDisabled: boolean;
  onSubmit: () => void;
}

export const renderProductIntro = ({ productInfo }: RenderProductIntroProps) => (
  <div className="flex flex-row gap-6 items-center w-full">
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img
      src={productInfo.image}
      alt={productInfo.name}
      className="w-[88px] h-[88px] tablet:w-[136px] tablet:h-[136px] shrink-0 rounded-lg overflow-hidden"
    />
    <div className="flex flex-col items-start gap-2">
      <div className="flex flex-row gap-2 items-center">
        <p className="text-label-medium font-semibold text-neutral-700">{productInfo.brand}</p>
        <p className="text-label-small font-medium text-primary-400">{decodeHTMLEntities(productInfo.category)}</p>
      </div>
      <p className="text-paragraph-small font-normal text-neutral-800">{productInfo.name}</p>
    </div>
  </div>
);

export const renderRatingSection = ({
  rating,
  setRating,
  SectionComponent: _SectionComponent = Section,
}: RenderRatingSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Overall rating*</p>
    <div className="flex flex-row w-full items-center justify-center">
      <div className="flex flex-row items-center gap-1 justify-center">
        {[1, 2, 3, 4, 5].map((num) => (
          <Star
            key={num}
            type="button"
            selected={rating >= num}
            onClick={() => setRating(num)}
            aria-label={`Rating ${num} stars`}
          >
            <StarIcon fillColor="#6F3FD7" isFill={rating >= num} />
          </Star>
        ))}
      </div>
    </div>
  </div>
);

export const renderSkinInfoSection = ({
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  skinTypeOptions,
  skinConcernOptions,
}: RenderSkinInfoSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">More about you*</p>
    <Row>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select options={skinTypeOptions} value={skinType} onChange={setSkinType} title="Skin type" showSelectedValue />
      </Section>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <Select
          options={skinConcernOptions}
          value={skinConcern}
          onChange={setSkinConcern}
          title="Skin concern"
          showSelectedValue
        />
      </Section>
    </Row>
  </div>
);

export const renderReviewContentSection = ({ content, setContent }: RenderReviewContentSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Your review*</p>
    <Textarea id="review-content" value={content} onChange={(e) => setContent(e.target.value)} />
  </div>
);

export const renderTitleSection = ({ title, setTitle }: RenderTitleSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Add a headline*</p>
    <Input
      id="review-title"
      maxLength={200}
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      placeholder="e.g. Super moisturizing, exactly what I needed"
    />
  </div>
);

export const renderImageUploadSection = ({
  images,
  imagePreviews,
  handleImageChange,
  handleRemoveImage,
}: RenderImageUploadSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Add a Photo</p>
    <ImageUploadBox>
      {imagePreviews.map((preview, idx) => (
        <Thumbnail key={preview.id}>
          <ThumbnailImage src={preview.url} alt={`Review photo ${idx + 1}`} />
          <RemoveButton type="button" onClick={() => handleRemoveImage(preview.id)} aria-label="Remove photo">
            <LucideIcon name="X" width="14px" height="14px" className="text-generic-100" />
          </RemoveButton>
        </Thumbnail>
      ))}
      {images.length < 5 &&
        (images.length === 0 ? (
          <UploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </UploadArea>
        ) : (
          <SmallUploadArea htmlFor="review-image">
            <PlusIcon />
            <FileInput id="review-image" type="file" accept="image/*" multiple onChange={handleImageChange} />
          </SmallUploadArea>
        ))}
    </ImageUploadBox>
  </div>
);

export const renderRecommendationSection = ({ recommend, setRecommend }: RenderRecommendationSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Repurchase Intention*</p>

    <RadioGroup name="recommend" value={recommend} onValueChange={setRecommend}>
      <div className="flex flex-row gap-4">
        <RadioGroupItem value="yes">Yes</RadioGroupItem>

        <RadioGroupItem value="no">No</RadioGroupItem>
      </div>
    </RadioGroup>
  </div>
);

export const renderNicknameSection = ({ nickname, setNickname }: RenderNicknameSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Create a display name*</p>
    <Input
      id="review-nickname"
      maxLength={25}
      value={nickname}
      onChange={(e) => setNickname(e.target.value)}
      placeholder="Enter your display name"
    />
  </div>
);

export const renderEmailSection = ({ email, setEmail }: RenderEmailSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Email address*</p>
    <Input
      id="review-email"
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="Enter your email address"
    />
  </div>
);

export const renderTermsSection = () => (
  <div className="flex flex-col gap-3 items-start w-full">
    <div className="flex flex-row items-start gap-2">
      <LucideIcon name="Info" width="16px" height="16px" className="text-neutral-500" />
      <p className="text-label-small font-normal text-neutral-500">
        We will email you to notify you that your review had been posted or if someone replies to your review.
      </p>
    </div>
  </div>
);

export const renderSubmitSection = ({ isSubmitDisabled, onSubmit }: RenderSubmitSectionProps) => (
  <SubmitButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
    Submit review
  </SubmitButton>
);

export const ReviewRewardBanner = () => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  return (
    <div
      className="flex flex-row p-3 tablet:p-1 tablet:pl-3 w-full rounded-[12px] gap-3 tablet:gap-6 items-center justify-between"
      style={{
        background: 'linear-gradient(107deg,rgba(18, 0, 58, 1) 0%, rgba(92, 34, 219, 1) 133.32%)',
      }}
    >
      <div className="flex flex-col items-start">
        <div className="flex flex-col items-start gap-0">
          {isMobile ? (
            <>
              <p className="text-paragraph-small font-medium whitespace-pre-wrap text-generic-0">
                Review your purchase
              </p>
              <p className="text-paragraph-small font-bold whitespace-pre-wrap text-generic-0">Get a coupon</p>
              <p className="text-paragraph-small font-bold whitespace-pre-wrap text-generic-0">for your next order !</p>
            </>
          ) : (
            <>
              <p className="text-paragraph-small font-medium whitespace-pre-wrap text-generic-0">
                Review your purchase
              </p>
              <p className="text-paragraph-small font-bold whitespace-pre-wrap text-generic-0">
                Get a coupon for your next order !
              </p>
            </>
          )}
        </div>
      </div>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={`${getMediaOrigin()}${REVIEW_IMAGE_PATHS.COUPON}`} alt="coin" className="w-[130px] h-[59px] shrink-0" />
    </div>
  );
};
